/**
 * DragGuide — the restrained gold directional relationship for the dedicated
 * drag lesson: a thin line with a small arrowhead from the required tile
 * toward its destination. Deliberately quiet:
 *
 *  - drawn from MEASURED real bounds (never hardcoded coordinates)
 *  - static (no motion → nothing to disable under reduced motion)
 *  - pointer-events: none — it can never intercept the drag
 *  - visually secondary to the real tile and destination rings
 *  - the screen removes it the moment the production drag begins
 *    (placement previews appearing) and after the drop/step exit.
 */
import type { SpotRect } from '../../tutorial/spotlight/spotlightGeometry';
import { SPOTLIGHT_Z } from './SpotlightOverlay';

const GOLD = 'rgba(201,168,76,0.75)';
/** Gap kept between the line's endpoints and each rect edge. */
const END_GAP = 14;

interface Props {
  /** The required tile's measured rect. */
  from: SpotRect;
  /** The destination's measured rect. */
  to: SpotRect;
}

export function DragGuide({ from, to }: Props) {
  const x1r = from.x + from.w / 2;
  const y1r = from.y + from.h / 2;
  const x2r = to.x + to.w / 2;
  const y2r = to.y + to.h / 2;
  const dx = x2r - x1r;
  const dy = y2r - y1r;
  const len = Math.hypot(dx, dy);
  if (len < from.h / 2 + to.h / 2 + END_GAP * 2) return null; // too close to draw usefully

  const ux = dx / len;
  const uy = dy / len;
  // Trim endpoints so the line starts just past the tile and ends just short
  // of the destination (the rings own those areas).
  const x1 = x1r + ux * (from.h / 2 + END_GAP);
  const y1 = y1r + uy * (from.h / 2 + END_GAP);
  const x2 = x2r - ux * (to.h / 2 + END_GAP);
  const y2 = y2r - uy * (to.h / 2 + END_GAP);

  // Small arrowhead at the destination end.
  const AH = 7;
  const leftX = x2 - ux * AH - uy * (AH * 0.7);
  const leftY = y2 - uy * AH + ux * (AH * 0.7);
  const rightX = x2 - ux * AH + uy * (AH * 0.7);
  const rightY = y2 - uy * AH - ux * (AH * 0.7);

  return (
    <svg
      data-testid="tut-drag-guide"
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: SPOTLIGHT_Z + 1,
      }}
    >
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={GOLD} strokeWidth={1.5} strokeDasharray="5 5" />
      <polyline
        points={`${leftX},${leftY} ${x2},${y2} ${rightX},${rightY}`}
        fill="none"
        stroke={GOLD}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
    </svg>
  );
}
